/* global google */
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash.debounce';
import { emptyAutocompleteValues, requestAutocompleteValues } from '@actions/dashboard-actions';
import {
  setGoogleAutocompleteSuggestions,
  setSearchTerm,
  showAutocompleteError
} from '@actions/map-actions';
import { colors } from '@constants/colors';
import { Icon, IconButton } from '@mui';
import { getConfigKey } from '@utils/config-utils';
import './input.scss';

/*
 * Global search input component.
 *
 * We handle in a single component the Google Places Autocomplete API with
 * our own backend autocomplete API.
 */
const Input = () => {
  const dispatch = useDispatch();
  const term = useSelector(state => state.map.traySearch.term);

  const autocompleteServiceRef = useRef(null);
  const autocompleteOKRef = useRef(null);
  const termRef = useRef(term);
  termRef.current = term;

  useEffect(() => {
    if (!window.google) {
      // See: https://github.com/kenny-hibino/react-places-autocomplete#load-google-library
      throw new Error('Google Maps JavaScript API library must be loaded');
    }

    if (!window.google.maps.places) {
      // If you see this error, add `libraries=places` to the src URL.
      // See: https://github.com/kenny-hibino/react-places-autocomplete#load-google-library
      throw new Error('Google Maps Places library must be loaded.');
    }

    autocompleteServiceRef.current = new google.maps.places.AutocompleteService();
    autocompleteOKRef.current = google.maps.places.PlacesServiceStatus.OK;
  }, []);

  const autocompleteCallback = useCallback((predictions, status) => {
    if (status !== autocompleteOKRef.current) {
      // If it's ZERO_RESULTS, skip showing that in
      // the SnackBar, since we already show 'No results found'
      // on the UI.
      // Only call the onError() callback if it's other
      // kind of error.
      if (status !== 'ZERO_RESULTS') {
        dispatch(showAutocompleteError(status));
      }
      dispatch(setGoogleAutocompleteSuggestions([]));
      return;
    }
    const stateRestriction = getConfigKey('autocompleteState');
    if (stateRestriction) {
      const filteredPredictions = predictions.filter(prediction => {
        if (prediction?.terms?.length > 1) {
          const stateIndex = prediction.terms.length - 2;
          const state = prediction.terms[stateIndex].value;
          // If we correctly matched the term to the "state" part
          // and it's not the state we have configured, skip the entry.
          if (state.length === 2 && state !== stateRestriction) {
            return false;
          }
        }
        return true;
      });
      dispatch(setGoogleAutocompleteSuggestions(filteredPredictions));
    } else {
      dispatch(setGoogleAutocompleteSuggestions(predictions));
    }
  }, [dispatch]);

  const fetchPredictions = useCallback(() => {
    const currentTerm = termRef.current;
    if (currentTerm.length) {
      autocompleteServiceRef.current.getPlacePredictions({
        locationRestriction: getConfigKey('autocompleteBounds'),
        componentRestrictions: { country: 'us' },
        input: currentTerm
      }, autocompleteCallback);
    } else {
      // Clear suggestions:
      dispatch(setGoogleAutocompleteSuggestions([]));
    }
  }, [autocompleteCallback, dispatch]);

  const fetchBackendSuggestions = useCallback(() => {
    const currentTerm = termRef.current;
    if (currentTerm.length) {
      dispatch(requestAutocompleteValues('common', currentTerm));
    } else {
      dispatch(emptyAutocompleteValues());
    }
  }, [dispatch]);

  const debouncedFetchPredictions = useMemo(
    () => debounce(fetchPredictions, 200),
    [fetchPredictions]
  );

  const debouncedFetchBackendSuggestions = useMemo(
    () => debounce(fetchBackendSuggestions, 500),
    [fetchBackendSuggestions]
  );

  // Cleanup debounced functions on unmount
  useEffect(() => {
    return () => {
      debouncedFetchPredictions.cancel();
      debouncedFetchBackendSuggestions.cancel();
    };
  }, [debouncedFetchBackendSuggestions, debouncedFetchPredictions]);

  const setTerm = useCallback(newTerm => {
    dispatch(setSearchTerm(newTerm));
    debouncedFetchPredictions();
    debouncedFetchBackendSuggestions();
  }, [debouncedFetchBackendSuggestions, debouncedFetchPredictions, dispatch]);

  const onChange = useCallback(event => {
    const value = event.target.value;
    setTerm(value);
  }, [setTerm]);

  const clear = useCallback(() => {
    setTerm('');
  }, [setTerm]);

  return (
    <div styleName="search-input">
      <div styleName="search-input-wrapper">
        <Icon color={colors.opacity.black0_4} role="img">search</Icon>
        <input
          type="text"
          onChange={onChange}
          placeholder="Search records and locations"
          value={term}
        />
        {term.length > 0 && (
          <IconButton style={{ padding: 0 }} aria-label="Close">
            <Icon color={colors.opacity.black0_6} onClick={clear} role="img">close</Icon>
          </IconButton>
        )}
      </div>
    </div>
  );
};

export default Input;
